import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";

import TestHeader from "../components/Test/TestHeader";
import TestFooter from "../components/Test/TestFooter";
import QuestionColumn from "../components/Test/QuestionColumn";
import ContextColumn from "../components/Test/ContextColumn";
import ColumnDivider from "../components/Test/ColumnDivider";
import ReviewNavigator from "../components/Test/ReviewNavigator";
import SubmitConfirmDialog from "../components/Test/SubmitConfirmDialog";
import DesmosCalculatorWindow from "../components/Test/DesmosCalculatorWindow";
import ReferenceWindow from "../components/Test/ReferenceWindow";

import useTestTimer from "../hooks/useTestTimer";
import useQuestionTiming from "../hooks/useQuestionTiming";
import useAssignmentAutosave from "../hooks/useAssignmentAutosave";
import useTestHydration from "../hooks/useTestHydration";
import useResizableColumns from "../hooks/useResizableColumns";

import "../styles/testLayout.css";

const TestQuestionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state } = useLocation();

  const [mode, setMode] = useState("in_progress");
  const isReadOnly = mode === "completed" || mode === "review";

  const packet = state?.packet ?? null;
  const questions = state?.questions ?? [];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [marked, setMarked] = useState([]);
  const [isHydrated, setIsHydrated] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showReference, setShowReference] = useState(false);

  const unansweredCount = answers.filter((a) => a === null).length;

  // ===== COLUMN RESIZE HOOK =====
  const {
    containerRef,
    leftWidth,
    rightWidth,
    startResize,
  } = useResizableColumns({
    initial: 50,
    min: 25,
    max: 75,
  });

  // ===== TIMER =====
  const { timeRemaining, setTimeRemaining } = useTestTimer(
    packet?.estimatedMinutes
  );

  // ===== QUESTION TIMING =====
  const { questionTimes, setQuestionTimes } = useQuestionTiming(
    questions,
    currentIndex,
    {}
  );

  const assignmentId = packet?.packetId || packet?.id;

  // ===== AUTOSAVE =====
  const { saveNow } = useAssignmentAutosave({
    user,
    packet,
    questions,
    answers,
    marked,
    questionTimes,
    currentIndex,
    timeRemaining,
    assignmentId,
    disabled: isReadOnly || isSubmitted,
  });

  // ===== GUARD =====
  useEffect(() => {
    if (!packet || questions.length === 0) {
      navigate("/student");
    }
  }, [packet, questions, navigate]);

  // ===== HYDRATION =====
  useTestHydration({
    user,
    packet,
    questions,
    setAnswers,
    setMarked,
    setCurrentIndex,
    setQuestionTimes,
    setTimeRemaining,
    setIsHydrated,
    setMode,
  });

  if (!packet || questions.length === 0 || !isHydrated) {
    return <div>Loading test…</div>;
  }

  const isReviewPage = currentIndex === questions.length;
  const currentQuestion = questions[currentIndex];

  // ===== HANDLERS =====
  const handleExit = async () => {
    if (!isReadOnly) {
      await saveNow();
    }
    navigate("/student");
  };

  const handleSubmit = async () => {
    if (isReadOnly || !isHydrated) return;

    if (unansweredCount > 0) {
      setShowSubmitConfirm(true);
      return;
    }

    await finalizeSubmit();
  };

  const computeCorrectCount = () => {
    return questions.reduce((count, q, i) => {
      if (!q?.correctAnswer) return count;
      return answers[i] === q.correctAnswer ? count + 1 : count;
    }, 0);
  };

  const finalizeSubmit = async () => {
    setIsSubmitted(true);
    const correctCount = computeCorrectCount();
    await saveNow({
      status: "completed",
      completedAt: new Date(),
      correct: correctCount,
    });
    navigate("/student");
  };

  const onToggleCalculator = () => {
    setShowCalculator((prev) => !prev);
  };

  const onToggleReference = () => {
    setShowReference((prev) => !prev);
  };

  return (
    <div
      ref={containerRef}
      className="test-page"
      style={{
        "--left-col": `${leftWidth}%`,
        "--right-col": `${rightWidth}%`,
      }}
    >
      <TestHeader
        timeRemaining={isReadOnly ? null : timeRemaining}
        onExit={handleExit}
        onToggleCalculator={onToggleCalculator}
        onToggleReference={onToggleReference}
        isCalculatorOpen={showCalculator}
        isReferenceOpen={showReference}
      />

      {showReference && (
        <ReferenceWindow onClose={() => setShowReference(false)} />
      )}

      {showCalculator && (
        <DesmosCalculatorWindow onClose={() => setShowCalculator(false)} />
      )}

      {isReviewPage ? (
        <div
          className="test-main"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ReviewNavigator
            currentIndex={currentIndex}
            answers={answers}
            marked={marked}
            onJump={setCurrentIndex}
          />
        </div>
      ) : (
        <div className="test-main">
          <ContextColumn
            passage={currentQuestion?.passage}
            context={currentQuestion?.context}
            imageUrl={currentQuestion?.imageUrl}
          />

          <ColumnDivider onMouseDown={startResize} />

          <QuestionColumn
            question={currentQuestion}
            questionIndex={currentIndex}
            totalQuestions={questions.length}
            selectedAnswer={answers[currentIndex]}
            isMarked={marked[currentIndex]}
            isReadOnly={isReadOnly}
            setAnswer={
              isReadOnly
                ? undefined
                : (letter) =>
                    setAnswers((prev) => {
                      const next = [...prev];
                      next[currentIndex] = letter;
                      return next;
                    })
            }
            toggleMark={
              isReadOnly
                ? undefined
                : () =>
                    setMarked((prev) => {
                      const next = [...prev];
                      next[currentIndex] = !next[currentIndex];
                      return next;
                    })
            }
          />
        </div>
      )}

      <TestFooter
        currentIndex={currentIndex}
        totalQuestions={questions.length}
        setCurrentIndex={setCurrentIndex}
        answers={answers}
        marked={marked}
        isReviewPage={isReviewPage}
        isReadOnly={isReadOnly}
        onSubmit={handleSubmit}
        onExit={handleExit}
      />

      <SubmitConfirmDialog
        open={showSubmitConfirm}
        unansweredCount={unansweredCount}
        onCancel={() => setShowSubmitConfirm(false)}
        onConfirm={finalizeSubmit}
      />
    </div>
  );
};

export default TestQuestionPage;
